import React, { useState, useRef } from "react";
import { uploadFileToBucket } from "@/utils/helper/aws-bucketUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoaderCircle } from "lucide-react";

const DummyUpload = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedRes, setUploadedRes] = useState<string | any>("");
  const [error, setError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError("");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }

    try {
      setIsUploading(true);
      setError("");

    
      const imageUrl = await uploadFileToBucket(file, "images");

      if (imageUrl) {
        setUploadedRes(imageUrl);
        setFile(null); 
        if (fileInputRef.current) {
          fileInputRef.current.value = ""; 
        }
  
      } else {
        setError("Upload failed - no URL returned");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setError("Upload failed: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setIsUploading(false);
    }
  };

  const clearForm = () => {
    setFile(null);
    setUploadedRes("");
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="container mx-auto py-8 max-w-xl">
      <h1 className="text-2xl font-bold mb-6">File Upload Demo</h1>

      <div className="space-y-6">
        <div className="space-y-2">
          <label htmlFor="file-input" className="block text-sm font-medium">
            Select File
          </label>
          <Input
            ref={fileInputRef}
            id="file-input"
            type="file"
            onChange={handleFileChange}
            disabled={isUploading}
            className="cursor-pointer"
          />
        </div>

        {file && (
          <div className="text-sm">
            <p>
              <span className="font-medium">File name:</span> {file.name}
            </p>
            <p>
              <span className="font-medium">Size:</span> {(file.size / 1024).toFixed(2)} KB
            </p>
            <p>
              <span className="font-medium">Type:</span> {file.type}
            </p>
          </div>
        )}

        <div className="flex space-x-3">
          <Button
            onClick={handleUpload}
            disabled={!file || isUploading}
            className="flex items-center"
          >
            {isUploading && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
            {isUploading ? "Uploading..." : "Upload File"}
          </Button>

          <Button variant="outline" onClick={clearForm} disabled={isUploading}>
            Clear
          </Button>
        </div>

        {error && <div className="text-red-500 text-sm">Error: {error}</div>}

        {uploadedRes && (
          <div className="border rounded-md p-4 space-y-3">
            <h3 className="font-medium">Upload Successful!</h3>
            <div className="break-all">
              <p className="text-sm font-medium mb-1">File Res key:</p>
              <p className="text-sm text-blue-600">{uploadedRes?.key}</p>
            </div>
            {/* {file && file.type.startsWith("image/") && (
              <div>
                <p className="text-sm font-medium mb-1">Preview:</p>
                <img
                  src={uploadedRes}
                  alt="Uploaded file preview"
                  className="max-h-60 rounded-md border"
                />
              </div>
            )} */}
          </div>
        )}
      </div>
    </div>
  );
};

export default DummyUpload;
